/* Form Component to Add or Edit Customer details*/

import { useEffect, useState } from "react";
import { Button, Form, Select, Row, Col } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import { TDropdownOptions, TResponseModel } from "../../../types/config";
import { eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { TCategoryConfig } from "../../../types";
import {
  AddEditIssueCategory,
  GetConfigParamDataList,
} from "../../../utils/api.constant";
import Utils from "../../../utils";
import { CONFIG_GROUPS } from "../../../utils/constants";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

type DrawerProps = {
  id: number;
  issueCatId: string;
  subIssueCatOneId: string;
  subIssueCatTwoId: string;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * CustomerForm component is used for adding or editing customer information.
 * It provides a form with fields for customer details such as name, code,
 * address, country, state, region, district, and zipcode. The component
 * handles fetching and setting dropdown options for countries, states,
 * cities, and regions based on the selected values. It also manages form
 * submission to add or update customer records.
 *
 * Props:
 * - id: number - The ID of the customer to edit; 0 for adding a new customer.
 * - onCloseDrawer: function - Callback function to close the form drawer.
 * - onRefreshList: function - Callback function to refresh the customer list
 *   after adding or editing a customer.
 */

export default function ConfigCategoryForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [issueCategoryOptions, setIssueCategoryOptions] = useState<
    TDropdownOptions[]
  >([]);
  const [subIssueCategoryOptions, setSubIssueCategoryOptions] = useState<
    TDropdownOptions[]
  >([]);
  const [subIssueCategoryOptions1, setSubIssueCategoryOptions1] = useState<
    TDropdownOptions[]
  >([]);

  const { post } = useFetch();
  const { onShowToast } = useToast();

  // Handle on submit event
  const onFinish = async (values: TCategoryConfig) => {
    try {
      const payload = {
        data: {
          id: props.id ? props.id : 0,
          ...values,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditIssueCategory, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        props.onCloseDrawer();
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = async (groupUniqueId: string) => {
    try {
      const payload = {
        data: {
          pageSize: -1,
          id: props.id,
          groupUniqueId: groupUniqueId,
        },
      };
      setIsLoading(true);

      const response: TResponseModel = await post(
        GetConfigParamDataList,
        payload
      );
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          return Utils.mapDropdownOptions(data);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchIssueCategory = async () => {
      const issueCategory = await fetchData(CONFIG_GROUPS.ISSUECATEGORY);
      setIssueCategoryOptions(issueCategory);
      const issueCategory1 = await fetchData(CONFIG_GROUPS.SUBISSUECATEGORY);
      setSubIssueCategoryOptions(issueCategory1);
      const issueCategory2 = await fetchData(CONFIG_GROUPS.SUBISSUECATEGORY1);
      setSubIssueCategoryOptions1(issueCategory2);
    };
    fetchIssueCategory();
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      issueCatId: props.issueCatId || "",
      subIssueCatOneId: props.subIssueCatOneId || "",
      subIssueCatTwoId: props.subIssueCatTwoId || "",
    });
  }, [props]);

  // To fetch specific config param
  /**
   * Fetches the list of service partners from the API.
   * Sets the isLoading state to true, sends a request with the current filterParams, and sets the servicePartnerOptions state with the response.
   * If the response is not successful, shows an error toast.
   * Finally, sets isLoading to false.
   */

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="issueCatId"
                label={<span>Issue Category</span>}
                rules={[
                  { required: true, message: "Please select ticket type" },
                ]}
              >
                <Select
                  placeholder="Select Issue Category"
                  options={issueCategoryOptions}
                  // form.resetFields();
                ></Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="subIssueCatOneId"
                label={<span>Sub Issue Category</span>}
                rules={[
                  {
                    required: true,
                    message: "Please Select Sub Issue Category ",
                  },
                ]}
              >
                <Select
                  placeholder="Select Sub Issue Category"
                  options={subIssueCategoryOptions}
                ></Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="subIssueCatTwoId"
                label={<span>Sub Issue Category 1</span>}
                rules={[
                  {
                    required: true,
                    message: "Please select Sub Issue Category 1",
                  },
                ]}
              >
                <Select
                  placeholder="Sub Issue Category 1"
                  options={subIssueCategoryOptions1}
                ></Select>
              </Form.Item>
            </Col>
          </Row>
        </FormContent>

        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              type="primary"
              className="btnDimensions"
              onClick={form.submit}
              disabled={isLoading}
            >
              {props.id ? "Update" : "Save"}
            </Button>
            <Button
              type="primary"
              className="btnDimensions"
              disabled={isLoading}
              onClick={() => {
                // form.resetFields();
                props.onCloseDrawer();
              }}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
